import { convertRgbToRgba, useTheme } from '@backoffice-frontend/patterns'
import { useMemo } from 'react'
import { useHereLocations, usePolygon } from '../hooks'
import type { Location } from '../hooks/useHereLocation'
import { useHereMap } from '../hooks/useHereMap'

type MapPolygonProps = {
  isCutout?: boolean
  fillColor?: string
  strokeColor?: string
  lineWidth?: number
  lineDash?: number[]
  onClick?: (location: Location) => void
  innerRings?: Location[][]
} & (
  | { points: Location[]; geoJson?: never }
  | { points?: never; geoJson: string }
)

/**
 * MapPolygon shows a polygon/area based on a list of points or a GeoJSON string.
 * It should be used as a direct child of the `HereMap` component.
 * @param points The points that make up the outline of the polygon.
 * @param geoJson A GeoJSON string (Polygon or MultiPolygon) as an alternative to points.
 * @param isCutout Let the inside of the polygon be transparent and the outside be filled.
 * @param strokeColor The color of the outline. Defaults to theme accent color.
 * @param fillColor The color of the inside/outside of the polygon. Defaults to strokeColor at 10% opacity.
 * @param lineWidth The width of the polygon outline.
 * @param lineDash The dash pattern of the polygon outline.
 * @param onClick Callback when the polygon is clicked, receives the geo location of the click. Cursor automatically changes to 'pointer' on hover.
 * @param innerRings Arrays of points defining holes in the polygon.
 */
export const MapPolygon = (props: MapPolygonProps) => {
  const theme = useTheme()
  const { map } = useHereMap()

  const points = useHereLocations(props.points)
  const strokeColor = props.strokeColor ?? theme.semantic.ColorAccentDefault

  const innerRings = useMemo(
    () =>
      props.innerRings?.map(ring =>
        ring.map(loc => ({ lat: loc.latitude, lng: loc.longitude })),
      ),
    [JSON.stringify(props.innerRings)],
  )

  usePolygon({
    map,
    points,
    geoJson: props.geoJson,
    isCutout: props.isCutout,
    strokeColor,
    fillColor: props.fillColor ?? convertRgbToRgba(strokeColor, 0.1),
    lineDash: props.lineDash,
    lineWidth: props.lineWidth,
    innerRings,
    onClick: props.onClick,
  })

  return null
}
